import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronDown, Globe2, Menu, Search, ShoppingBag, UserCircle, MapPin } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useCartActions, useCartState } from '@/context/CartContext';
import { useTheme } from '@/context/ThemeContext';
import { useBrandsQuery } from '@/data/brands.queries';
import { useCategoriesQuery } from '@/data/categories.queries';
import { fallbackBrands, fallbackCategories } from '@/data/ubuy-home.mock';
import UbuyMegaMenu from './UbuyMegaMenu';
import UbuyMobileDrawer from './UbuyMobileDrawer';

export default function UbuyHeader() {
  const navigate = useNavigate();
  const { cartCount } = useCartState();
  const { setIsCartOpen } = useCartActions();
  const { language, toggleLanguage } = useTheme();
  const { user, logout } = useAuth();
  const { data: categories = [] } = useCategoriesQuery();
  const { data: brands = [] } = useBrandsQuery();
  const displayCategories = categories.length ? categories : fallbackCategories;
  const displayBrands = brands.length ? brands : fallbackBrands;
  const [query, setQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = query.trim();
    if (!trimmed) {
      navigate('/products');
      return;
    }
    navigate(`/products?search=${encodeURIComponent(trimmed)}`);
  };

  return (
    <header className="sticky left-0 right-0 top-0 z-50 bg-white shadow-[var(--ubuy-header-shadow)]">
      <div className="mx-auto flex h-[46px] max-w-[1395px] items-center gap-2 px-[15px] lg:h-[72px]">
        <button
          type="button"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ubuy-border-soft)] lg:hidden"
          onClick={() => setDrawerOpen(true)}
          aria-label={language === 'mn' ? 'Цэс нээх' : 'Open menu'}
        >
          <Menu size={19} />
        </button>

        <Link to="/" className="flex shrink-0 items-center">
          <img src="/kg-goods-light-.svg" alt="KG Goods" className="h-9 w-auto lg:h-12" />
        </Link>

        <nav className="ml-2 hidden h-full items-center gap-1 lg:flex">
          <Link
            to="/products?sale=true"
            className="rounded-lg bg-[var(--ubuy-accent)] px-4 py-2 text-sm font-bold text-[var(--ubuy-title)]"
          >
            {language === 'mn' ? 'Өнөөдрийн deal' : "Today's Deals"}
          </Link>
          <div className="group relative flex h-full items-center">
            <button className="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-[var(--ubuy-title)] hover:bg-[var(--ubuy-subtle-surface)]">
              Brands
              <ChevronDown size={15} />
            </button>
            <UbuyMegaMenu language={language} categories={displayCategories} brands={displayBrands} />
          </div>
          <div className="group relative flex h-full items-center">
            <button className="flex items-center gap-1 rounded-lg px-4 py-2 text-sm font-semibold text-[var(--ubuy-title)] hover:bg-[var(--ubuy-subtle-surface)]">
              {language === 'mn' ? 'Ангилал' : 'Categories'}
              <ChevronDown size={15} />
            </button>
            <UbuyMegaMenu language={language} categories={displayCategories} brands={displayBrands} />
          </div>
        </nav>

        <form onSubmit={submitSearch} className="ml-auto hidden min-w-[220px] flex-1 items-center xl:flex">
          <label className="relative w-full">
            <span className="sr-only">{language === 'mn' ? 'Бараа хайх' : 'Search products'}</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-[var(--ubuy-muted)]" size={18} />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={language === 'mn' ? 'Бараа, brand эсвэл ангилал хайх' : 'Search products, brands, or categories'}
              className="h-11 w-full rounded-[50px] border border-[var(--ubuy-border)] bg-white pl-11 pr-4 text-sm outline-none shadow-[var(--ubuy-search-shadow)] focus:border-[var(--ubuy-accent)]"
            />
          </label>
        </form>

        <div className="ml-auto flex shrink-0 items-center gap-1.5 xl:ml-2">
          <button
            type="button"
            onClick={() => navigate('/products')}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--ubuy-border-soft)] text-[var(--ubuy-title)] xl:hidden"
            aria-label={language === 'mn' ? 'Хайлт' : 'Search'}
          >
            <Search size={18} />
          </button>

          <Link
            to="/cargo-branches"
            className="hidden items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-[var(--ubuy-title)] hover:bg-[var(--ubuy-subtle-surface)] lg:flex"
          >
            <MapPin size={16} className="text-[var(--ubuy-accent)]" />
            Mongolia
          </Link>


          <button
            type="button"
            onClick={toggleLanguage}
            className="hidden items-center gap-1 rounded-lg px-3 py-2 text-xs font-bold uppercase text-[var(--ubuy-title)] hover:bg-[var(--ubuy-subtle-surface)] md:flex"
          >
            <Globe2 size={16} />
            {language}
          </button>

          <div className="group relative hidden md:block">
            <button className="flex items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold text-[var(--ubuy-title)] hover:bg-[var(--ubuy-subtle-surface)]">
              <UserCircle size={18} />
              {user ? (language === 'mn' ? 'Аккаунт' : 'Account') : language === 'mn' ? 'Нэвтрэх' : 'Login'}
              <ChevronDown size={14} />
            </button>
            <div className="absolute right-0 top-full hidden w-56 pt-3 group-hover:block">
              <div className="rounded-[12px] border border-[var(--ubuy-border-soft)] bg-white p-2 shadow-[0_12px_32px_rgba(0,0,0,0.14)]">
                {user ? (
                  <>
                    <p className="truncate px-3 py-2 text-xs text-[var(--ubuy-muted)]">{user.email}</p>
                    <Link to="/orders" className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--ubuy-subtle-surface)]">
                      {language === 'mn' ? 'Миний захиалга' : 'My orders'}
                    </Link>
                    <Link to="/profile" className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--ubuy-subtle-surface)]">
                      {language === 'mn' ? 'Профайл' : 'Profile'}
                    </Link>
                    <button
                      type="button"
                      onClick={logout}
                      className="block w-full rounded-lg px-3 py-2 text-left text-sm text-[var(--ubuy-danger)] hover:bg-[var(--ubuy-subtle-surface)]"
                    >
                      {language === 'mn' ? 'Гарах' : 'Logout'}
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--ubuy-subtle-surface)]">
                      {language === 'mn' ? 'Нэвтрэх' : 'Login'}
                    </Link>
                    <Link to="/signup" className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--ubuy-subtle-surface)]">
                      {language === 'mn' ? 'Бүртгүүлэх' : 'Create account'}
                    </Link>
                  </>
                )}
                <Link to="/wishlist" className="block rounded-lg px-3 py-2 text-sm hover:bg-[var(--ubuy-subtle-surface)]">
                  Wishlist
                </Link>
              </div>
            </div>
          </div>

          <button
            type="button"
            data-testid="cart-icon"
            onClick={() => setIsCartOpen(true)}
            className="relative flex h-10 w-10 items-center justify-center rounded-full bg-[var(--ubuy-title)] text-white transition hover:bg-[var(--ubuy-accent)] hover:text-[var(--ubuy-title)]"
            aria-label={language === 'mn' ? 'Сагс' : 'Cart'}
          >
            <ShoppingBag size={18} />
            {cartCount > 0 ? (
              <span
                data-testid="cart-badge"
                className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--ubuy-accent)] px-1 text-[11px] font-bold text-[var(--ubuy-title)]"
              >
                {cartCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      <UbuyMobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        language={language}
        onLanguageToggle={toggleLanguage}
        categories={displayCategories}
        brands={displayBrands}
        cartCount={cartCount}
      />
    </header>
  );
}
